// Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { Box, Button, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { finishLine, routes } from "../../config/constant";
import { Rating } from "@mui/material";
import { useNavigate } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Stack } from "@mui/system";
import { RootState, useAppDispatch, useAppSelector } from "../../slices/store";
import { addUserFeedback, setState } from "../../slices/examSlice/userFeedback";
import { LoadingButton } from "@mui/lab";

export const RatingForm = (props: any) => {
  const dispatch = useAppDispatch();
  const contactState = useAppSelector((state: RootState) => state.contact);
  const feedbackState = useAppSelector(
    (state: RootState) => state.userFeedback
  );

  const [q1Rating, setQ1Rating] = useState<number | null>(0);
  const [q2Rating, setQ2Rating] = useState<number | null>(0);
  const [q3Rating, setQ3Rating] = useState<number | null>(0);
  const [q4Rating, setQ4Rating] = useState<number | null>(0);
  const [comment, setComment] = useState("");

  const [showSurvey, setShowSurvey] = useState<boolean>(false);

  let navigate = useNavigate();

  useEffect(() => {
    if (feedbackState.state == "success") {
      dispatch(setState("idle"));
      navigate(routes.exams);
    }
  }, [feedbackState.state]);

  const submitFeedback = () => {
    const date = new Date();

    let day = date.getDate();
    let month = date.getMonth() + 1;
    let year = date.getFullYear();
    var feedback = {
      date: `${month}/${day}/${year}`,
      certificationId: `${contactState.response.certificationId}`,
      certificationType: props.certificationType.toString(),
      registration: q1Rating,
      materials: q2Rating,
      clarity: q3Rating,
      recommendation: q4Rating,
      feedback: comment,
    };

    dispatch(addUserFeedback(feedback));
  };

  return (
    <Box>
      <Typography sx={{ fontWeight: "bold" }} gutterBottom variant="subtitle2">
        {finishLine.feedback}
      </Typography>
      {!showSurvey && (
        <Button
          onClick={() => setShowSurvey(!showSurvey)}
          startIcon={<KeyboardArrowDownIcon />}
          size="small"
          variant="outlined"
          sx={{
            color: "text.secondary",
            px: 1,
            fontSize: "0.75rem",
            mt: 1,
          }}
        >
          Take Survey
        </Button>
      )}

      {showSurvey && (
        <Box ml={2} mt={3}>
          <Typography fontWeight={550} gutterBottom sx={{ mb: 3 }}>
            {finishLine.feedbackIntro}
          </Typography>
          <Box sx={{ ml: 3 }}>
            <Typography variant="body2" component="legend">
              {finishLine.feedbackQ1}
            </Typography>
            <Rating
              sx={{ mb: 3, mt: 1 }}
              name="q1"
              onChange={(event, newValue) => {
                setQ1Rating(newValue);
              }}
            />
            <Typography variant="body2" component="legend">
              {finishLine.feedbackQ2}
            </Typography>
            <Rating
              sx={{ mb: 3, mt: 1 }}
              name="q2"
              onChange={(event, newValue) => {
                setQ2Rating(newValue);
              }}
            />
            <Typography variant="body2" component="legend">
              {finishLine.feedbackQ3}
            </Typography>
            <Rating
              sx={{ mb: 3, mt: 1 }}
              name="q3"
              onChange={(event, newValue) => {
                setQ3Rating(newValue);
              }}
            />
            <Typography variant="body2" component="legend">
              {finishLine.feedbackQ4}
            </Typography>
            <Rating
              sx={{ mb: 3, mt: 1 }}
              name="q4"
              onChange={(event, newValue) => {
                setQ4Rating(newValue);
              }}
            />
            <Typography variant="body2" component="legend">
              {finishLine.feedbackQ5}
            </Typography>
            <TextField
              fullWidth
              multiline
              onChange={(value) => setComment(value.target.value)}
              rows={2}
            />
            <Stack mt={2} direction="row" spacing={2}>
              <Button
                size="small"
                onClick={() => setShowSurvey(!showSurvey)}
                sx={{ px: 1, fontWeight: 500, fontSize: "0.75rem" }}
                variant="outlined"
              >
                Cancel
              </Button>
              <LoadingButton
                loading={feedbackState.state === "loading"}
                size="small"
                onClick={submitFeedback}
                disabled={
                  q1Rating === 0 ||
                  q2Rating === 0 ||
                  q3Rating === 0 ||
                  q4Rating === 0
                }
                sx={{ px: 1, fontWeight: 500, fontSize: "0.75rem" }}
                variant="contained"
                autoFocus
                translate="no"
              >
                Submit Feedback
              </LoadingButton>
            </Stack>
          </Box>
        </Box>
      )}
    </Box>
  );
};
